/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {MenuGroupType} from '@app/components/uikit/menu_bottom_sheet/MenuBottomSheet';
import {MenuBottomSheet} from '@app/components/uikit/menu_bottom_sheet/MenuBottomSheet';
import DiscoveryStore from '@app/stores/DiscoveryStore';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

interface DiscoveryCategoryBottomSheetProps {
	isOpen: boolean;
	onClose: () => void;
}

export const DiscoveryCategoryBottomSheet = observer(function DiscoveryCategoryBottomSheet({
	isOpen,
	onClose,
}: DiscoveryCategoryBottomSheetProps) {
	const {t} = useLingui();

	const handleCategorySelect = useCallback(
		(categoryId: number | null) => {
			void DiscoveryStore.search({category: categoryId, offset: 0});
			onClose();
		},
		[onClose],
	);

	const groups: Array<MenuGroupType> = useMemo(
		() => [
			{
				items: [
					{
						label: t`All`,
						onClick: () => handleCategorySelect(null),
					},
					...DiscoveryStore.categories.map((cat) => ({
						label: cat.name,
						onClick: () => handleCategorySelect(cat.id),
					})),
				],
			},
		],
		[DiscoveryStore.categories, handleCategorySelect, t],
	);

	return <MenuBottomSheet isOpen={isOpen} onClose={onClose} groups={groups} title={t`Categories`} />;
});
